import { createMemo, DEV } from "solid-js"
import type { Nullable } from "vite-node"
import Unexpected from "../../../Exception/Unexpected.class"
import isNil from "../../../ldsh/isNil"
import isNotNil from "../../../ldsh/isNotNil"
import eightDirectionCharsLowerCase, { type EightDirectionIndex } from "../../direction/eightDirectionCharsLowerCase"
import getOppositeDirectionIndex from "../../direction/getOppositeDirectionIndex"
import type { TileGraphCorner } from "./TileGraphCorner.type"
import getEightDirectionIndexFrom from "./getEightDirectionIndexFrom"

export default function createCornerEightDirectionIndexMemo(
  nodeByLcMap: TileGraphCorner['node'],
  getNodesDesc: TileGraphCorner['node']['desc'],
) {
  // eslint-disable-next-line solid/reactivity
  return createMemo((): Nullable<EightDirectionIndex> => {
    const [node0, node1, node2, node3] = getNodesDesc()

    // if node0 is the highest elevation node
    if (node0 && (!node1 || node0.floor_e() > node1.floor_e())) {
      return getEightDirectionIndexFrom(nodeByLcMap, node0)
    }
    // remaining: node0 and node1 are the same elevation

    if (node1 && (!node2 || node1.floor_e() > node2.floor_e())) {
      // scenario 1: node0 and node1 are adjacent
      // scenario 2: node0 and node1 are diagonal (opposite)
      const edIndex0: number = getEightDirectionIndexFrom(nodeByLcMap, node0)!
      const edIndex1: number = getEightDirectionIndexFrom(nodeByLcMap, node1)!
      if (DEV) {
        if (isNil(edIndex0)) {
          throw new Unexpected('!edIndex0')
        }
        if (isNil(edIndex1)) {
          throw new Unexpected('!edIndex1')
        }
      }
      const diff = Math.abs(edIndex0 - edIndex1)
      if (diff === 2) {
        const mutualIndex = ((edIndex0 + edIndex1) / 2) as EightDirectionIndex
        return mutualIndex
        // not sure why the mutualIndex is the one that renders correctly
        // return getOppositeDirectionIndex(eightDirectionCharsLowerCase, mutualIndex)
      }
      // edge case, one of these is 1 and the other is 7
      if (diff === 6) {
        // therefor, the mutualIndex is 0 and the opposite is 4
        return 0
        // not sure why the mutualIndex is the one that renders correctly
        // return 4
      }

      // remaining: node0 and node1 are diagonal (adjacent)
      // just return the opposite index of node0
      // it's probably good enough.
      return getOppositeDirectionIndex(eightDirectionCharsLowerCase, edIndex0) as EightDirectionIndex
    }

    if (node2 && (!node3 || node2.floor_e() > node3.floor_e())) {
      // scenario: the highest three are the same elevation.
      // cdi must point to the lowest elevation node
      const toLowestIndex = getEightDirectionIndexFrom(nodeByLcMap, node3)
      // return toLowestIndex
      return isNotNil(toLowestIndex)
        ? (getOppositeDirectionIndex(eightDirectionCharsLowerCase, toLowestIndex as number) as EightDirectionIndex)
        : null
    }

    return null
  })


}